import { useState, useMemo } from "react";
import { useGetList } from "ra-core";
import { Check, ChevronsUpDown, User, Plus } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import type { Contact } from "../../providers/fakerest/dataGenerator/types";

interface ContactSearchSelectProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  onCreateNew?: () => void;
}

export const ContactSearchSelect = ({
  value,
  onChange,
  placeholder = "Select a contact...",
  disabled = false,
  onCreateNew,
}: ContactSearchSelectProps) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  // Fetch contacts
  const { data: contacts } = useGetList<Contact>("contacts", {
    pagination: { page: 1, perPage: 500 },
    sort: { field: "name", order: "ASC" },
  });

  // Filter contacts based on search
  const filteredContacts = useMemo(() => {
    if (!contacts) return [];
    if (!search) return contacts;

    const searchLower = search.toLowerCase();
    return contacts.filter(
      (contact) =>
        contact.name.toLowerCase().includes(searchLower) ||
        contact.email?.toLowerCase().includes(searchLower) ||
        contact.phone?.includes(search),
    );
  }, [contacts, search]);

  // Find selected contact
  const selectedContact = useMemo(() => {
    if (!value || !contacts) return null;
    return contacts.find((contact) => String(contact.id) === value);
  }, [value, contacts]);

  const handleSelect = (contactId: string) => {
    onChange(contactId);
    setOpen(false);
    setSearch("");
  };

  const handleCreateNew = () => {
    setOpen(false);
    setSearch("");
    onCreateNew?.();
  };

  return (
    <Popover open={open} onOpenChange={setOpen} modal>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between h-auto min-h-10 py-2 font-normal"
          disabled={disabled}
        >
          {selectedContact ? (
            <div className="flex items-center gap-2 text-left">
              <User className="h-4 w-4 text-muted-foreground shrink-0" />
              <span className="truncate">{selectedContact.name}</span>
            </div>
          ) : (
            <span className="text-muted-foreground">{placeholder}</span>
          )}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-full min-w-[var(--radix-popover-trigger-width)] p-0">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search by name, email, or phone..."
            value={search}
            onValueChange={setSearch}
          />
          <CommandList>
            {onCreateNew && (
              <CommandGroup>
                <CommandItem
                  onSelect={handleCreateNew}
                  className="py-2 text-primary"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Create new contact
                </CommandItem>
              </CommandGroup>
            )}
            {onCreateNew && <CommandSeparator />}
            <CommandEmpty>No contacts found.</CommandEmpty>
            <CommandGroup>
              {filteredContacts.map((contact) => (
                <CommandItem
                  key={contact.id}
                  value={String(contact.id)}
                  onSelect={() => handleSelect(String(contact.id))}
                  className="py-2"
                >
                  {value === String(contact.id) && (
                    <Check className="h-4 w-4 mr-2 shrink-0" />
                  )}
                  <User className="h-4 w-4 mr-2 text-muted-foreground shrink-0" />
                  <div className="flex flex-col min-w-0">
                    <span className="truncate">{contact.name}</span>
                    {contact.email && (
                      <span className="text-sm text-muted-foreground truncate">
                        {contact.email}
                      </span>
                    )}
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
